/**
 * Round B U6 — the Home card actions of an Azure Storage connection. The SQL actions (New Query, Explorer, Designer,
 * Overview, Dashboard) all refuse storage, so the card offered five disabled buttons; these are the ones that apply:
 * one entry per service folder the tree shows (it is revealed and selected in the Object Explorer), Transfers, Copy URL
 * (the SAS-free endpoint or attached resource) and Connection info.
 */
import { useMemo, type ReactNode } from 'react';
import { ArrowDownUp, Cloud, Container, FolderTree, HardDrive, Info, Link2, MessageSquare, Table } from 'lucide-react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { Button } from '../../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/tooltip';
import { toast } from '../../../../components/ui/toaster';
import { writeClipboardText } from '../../../../lib/clipboard';
import type { Connection } from '../../../../types/electron';
import type { StorageResourceRef, StorageService } from '../../../../types/azureStorage';
import { useDatabaseStore } from '../../../database-objects/store/databaseStore';
import { findNodeById } from '../../../explorer/services/treeService';
import { createConnectionNodeId } from '../../../explorer/utils/nodeIdUtils';
import type { StorageServiceFolderNode } from '../../../explorer/models/nodeTypes';
import { openTransfersPanel } from '../../../transfers/services/openTransfersPanel';
import { storageResourceUrl } from '../../storage-explorer/utils/storageUrls';
import { revealInObjectExplorer } from '../../../explorer/utils/revealInObjectExplorer';

type Props = {
  connection: Connection;
  isConnected: boolean;
  onConnectionInfo?: (connection: Connection) => void;
};

const SERVICE_ICONS: Record<StorageService, ReactNode> = {
  blob: <Container className="h-3.5 w-3.5" />,
  table: <Table className="h-3.5 w-3.5" />,
  queue: <MessageSquare className="h-3.5 w-3.5" />,
  file: <HardDrive className="h-3.5 w-3.5" />,
};

type HomeStorageAction = 'reveal_service' | 'reveal_connection' | 'transfers' | 'copy_url' | 'connection_info';

function Action({ icon, label, tooltip, onClick, testId }: { icon: ReactNode; label: string; tooltip: string; onClick: () => void; testId: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* C2 R6 / N3: the label wraps ("Data Lake Containers" on two lines) instead of "Blob Conta…". */}
          <Button variant="ghost" size="sm" className="h-auto min-h-8 w-full min-w-0 items-start justify-start gap-1.5 whitespace-normal px-2 py-1.5 text-left [&_svg]:mt-px" onClick={onClick} data-testid={testId}>
            {icon}
            <span className="min-w-0 break-words text-xs leading-tight">{label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom"><p>{tooltip}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** The resource Copy URL names: the attached resource, else the first ticked service's endpoint (Blob first). */
function urlResource(connection: Connection): StorageResourceRef {
  const scope = connection.azureStorageScope;
  if (scope?.kind === 'resource') return scope.resource;
  const services = connection.azureStorageServices ?? ['blob'];
  return { kind: 'service', service: services.includes('blob') ? 'blob' : services[0] ?? 'blob' };
}

export function StorageQuickActions({ connection, isConnected, onConnectionInfo }: Props) {
  const { track } = useAnalytics();
  const connectionNodeId = createConnectionNodeId(connection.id);
  // The children array is the store's own (stable) reference; the filter runs only when it changes.
  const children = useDatabaseStore((state) => findNodeById(connectionNodeId, state.treeData)?.children);
  const folders = useMemo(
    () => (children ?? []).filter((child): child is StorageServiceFolderNode => child.type === 'storage-service-folder'),
    [children],
  );
  const fire = (action: HomeStorageAction) => track('feature:storage_quick_action', { action, surface: 'home' });

  const copyUrl = async () => {
    const url = storageResourceUrl(connection, urlResource(connection));
    if (!url) { toast.error({ title: 'Copy URL', description: 'No endpoint is known for this connection' }); return; }
    if (await writeClipboardText(url)) {
      fire('copy_url'); // a successful side-effect: tracked after the copy, never on click
      toast.success({ title: 'URL copied', description: 'The endpoint URL, without any key or SAS.' });
    }
  };

  return (
    <div className="grid grid-cols-2 gap-1 pt-2 border-t border-border/50" data-testid="quick-actions-bar" data-variant="storage">
      {isConnected && folders.length > 0 ? folders.map((folder) => (
        <Action
          key={folder.id}
          icon={SERVICE_ICONS[folder.metadata.service] ?? <Cloud className="h-3.5 w-3.5" />}
          label={folder.name}
          tooltip={`Open ${folder.name} in the Object Explorer`}
          testId={`quick-action-storage-${folder.metadata.service}`}
          onClick={() => { fire('reveal_service'); revealInObjectExplorer(connection, folder.id); }}
        />
      )) : (
        <Action
          icon={<FolderTree className="h-3.5 w-3.5" />}
          label="Browse"
          tooltip="Show this connection in the Object Explorer"
          testId="quick-action-storage-browse"
          onClick={() => { fire('reveal_connection'); revealInObjectExplorer(connection, connectionNodeId); }}
        />
      )}
      <Action
        icon={<ArrowDownUp className="h-3.5 w-3.5" />}
        label="Transfers"
        tooltip="Open the Transfers panel (uploads, downloads and storage changes)"
        testId="quick-action-storage-transfers"
        onClick={() => {
          fire('transfers');
          openTransfersPanel('home'); // A+B merge: the drawer (and its panel_open event) know it came from the Home card
        }}
      />
      <Action
        icon={<Link2 className="h-3.5 w-3.5" />}
        label="Copy URL"
        tooltip="Copy the endpoint URL (no key or SAS)"
        testId="quick-action-storage-copy-url"
        onClick={() => void copyUrl()}
      />
      {onConnectionInfo && (
        <Action
          icon={<Info className="h-3.5 w-3.5" />}
          label="Connection info"
          tooltip="Account, endpoints, authentication and services"
          testId="quick-action-storage-info"
          onClick={() => { fire('connection_info'); onConnectionInfo(connection); }}
        />
      )}
    </div>
  );
}
